import { CartItemOptionsSubprogramsRegistration } from '@la/types';
import {
  AddCreditCardRequestData,
  BillingSummaryData,
  BillingSummaryRequestItem,
  CartSummaryData,
  CheckoutSummaryData,
  CombinedCartItemData,
  PaymentMethodType,
  ProcessPaymentBody,
  StoredCreditCardResponseData,
  WaiverData,
} from 'domains/ConsolidatedCheckout/Checkout.types';
import baseApi from './baseApi';

// Define a service using a base URL and injected endpoints
export const checkoutApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['BillingSummary', 'CartSummary', 'PaymentMethodSummary'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      addCreditCard: builder.mutation<
        StoredCreditCardResponseData,
        {
          body: AddCreditCardRequestData;
          siteId: string | null;
          userId?: number;
        }
      >({
        query: ({ siteId, userId, body }) => ({
          url: `/api/sites/${siteId}/users/${userId}/creditCards/stored/create`,
          method: 'POST',
          body: body,
        }),
        invalidatesTags: ['CartSummary'],
      }),
      getCheckoutSummaryBFF: builder.query<
        CheckoutSummaryData,
        {
          subdomain: string;
        }
      >({
        query: ({ subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/checkout`,
            method: 'GET',
          };
        },
      }),
      updateCheckoutSummaryBFF: builder.mutation<
        any,
        {
          combinedCartItems: CombinedCartItemData[];
          discountCodes?: string[];
          paymentMethod: string;
          subdomain: string;
        }
      >({
        query: ({
          combinedCartItems,
          discountCodes,
          paymentMethod,
          subdomain,
        }) => ({
          url: `/api/member-portal/${subdomain}/checkout`,
          method: 'POST',
          body: {
            combinedCartItems: combinedCartItems,
            discountCodes: discountCodes,
            paymentMethod: paymentMethod,
          },
        }),
        // providesTags: ['CartSummary', 'BillingSummary', 'PaymentMethodSummary'],
      }),
      deletePlayerStaffRegistration: builder.mutation<
        { message: string },
        {
          cartItemId: string;
          operation: string;
          registration: CartItemOptionsSubprogramsRegistration;
          subdomain: string;
          subprogramId: number;
        }
      >({
        query: ({ cartItemId, registration, subdomain, subprogramId }) => ({
          url: `/api/member-portal/${subdomain}/checkout`,
          method: 'PATCH',
          body: {
            cartItemId: cartItemId,
            operation: 'delete',
            registration: registration,
            subprogramId: subprogramId,
          },
        }),
      }),
      deleteCartItem: builder.mutation<
        CartSummaryData,
        {
          siteDomain: string;
          cartItemId: string;
        }
      >({
        query: ({ cartItemId, siteDomain }) => ({
          url: `/api/member-portal/${siteDomain}/cart`,
          method: 'DELETE',
          body: {
            cartItemUuid: cartItemId,
          },
        }),
        invalidatesTags: ['CartSummary', 'Registration'],
      }),
      processCart: builder.mutation<
        void,
        {
          body: ProcessPaymentBody;
          siteId: string | null;
          userId?: number;
        }
      >({
        query: ({ body, siteId, userId }) => ({
          url: `/api/cart/sites/${siteId}/users/${userId}/cart/process`,
          method: 'POST',
          body: body,
        }),
        invalidatesTags: ['CartSummary', 'Registration'],
      }),
      getPaymentWaiver: builder.query<
        WaiverData | undefined,
        { siteId: string | null }
      >({
        query: ({ siteId }) => `api/sites/${siteId}/waivers`,
        transformResponse: (waivers: WaiverData[]) => {
          return waivers.find((waiver) => waiver.waiverType === 'PAYMENT');
        },
      }),
      addDiscountCodes: builder.mutation<
        BillingSummaryData,
        {
          discountCodes?: string[];
          paymentMethod: PaymentMethodType;
          paymentTerms?: BillingSummaryRequestItem[];
          subdomain: string;
        }
      >({
        query: ({ subdomain, paymentMethod, paymentTerms, discountCodes }) => {
          return {
            url: `/api/member-portal/${subdomain}/checkout/billingSummary`,
            method: 'POST',
            body: {
              paymentMethod: paymentMethod,
              paymentTerms: paymentTerms,
              discountCodes: discountCodes,
            },
          };
        },
      }),
    }),
  });

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useAddCreditCardMutation,
  useDeleteCartItemMutation,
  useDeletePlayerStaffRegistrationMutation,
  useGetCheckoutSummaryBFFQuery,
  useGetPaymentWaiverQuery,
  useProcessCartMutation,
  useUpdateCheckoutSummaryBFFMutation,
} = checkoutApi;
